import {
	DerogationType,
	DerogationTypeValues,
	OffersWarrantyType,
	PricingDetailsFormValues,
	UpdateConstructionSiteDerogationsBody,
} from "@dune/types";
import { Button } from "@dune/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGetConstructionSiteDetails } from "../../../../api/queries/constructionSite.queries";
import { usePricingDetailsQueries } from "../../../../api/queries/pricing-details.queries";
import {
	useGetConstructionSiteDerogations,
	useUpdateConstructionSiteDerogations,
} from "../../../../api/queries/constructionSiteSubmission.queries";
import { useRequiredParams } from "../../../../hooks/useRequiredParams";
import { DerogationsCard } from "./components/DerogationsCard";
import { isOfferWarrantyType } from "@dune/utils";
import { pricingDetailsFormValuesToCreatePayload } from "../../../pricing/hooks/usePricingDetailsForm";
import { calculateTotal } from "../../../pricing/components/PricingDetailsForm";
import { format } from "date-fns";

interface DerogationStepProps {
	handlePreviousStep: () => void;
	handleNextStep: () => void;
	handleClose: () => void;
}

export type DerogationForm = {
	enabled: boolean;
	type: DerogationTypeValues | null;
	newConstructionCost: string;
	newDeliveryDate: string;
};

export type DerogationsForm = {
	derogations: DerogationForm[];
	newPricingDetails: Record<OffersWarrantyType, PricingDetailsFormValues>;
	selectedOffersIds: number[];
};

export const DerogationStep = ({
	handlePreviousStep,
	handleNextStep,
	handleClose,
}: DerogationStepProps) => {
	const { constructionSiteId } = useRequiredParams<{
		constructionSiteId: string;
	}>();

	const { data: constructionSite } =
		useGetConstructionSiteDetails(constructionSiteId);
	const constructionSiteOffers = constructionSite?.constructionSiteOffers;
	const constructionSiteOffersProducts = useMemo(() => {
		return constructionSiteOffers?.filter((offer) =>
			isOfferWarrantyType(offer.binderWarranty?.warrantyType),
		);
	}, [constructionSiteOffers]);

	const { data: existingDerogations } = useGetConstructionSiteDerogations(
		Number(constructionSiteId),
	);

	const updateDerogationsMutation = useUpdateConstructionSiteDerogations(
		Number(constructionSiteId),
	);

	const [currentAction, setCurrentAction] = useState<
		"save" | "saveAndNext" | "saveAndClose" | null
	>(null);

	const [showValidationErrors, setShowValidationErrors] = useState(false);

	const [derogationsForm, setDerogationsForm] = useState<DerogationsForm>({
		derogations: [],
		newPricingDetails: {
			DO: {},
			MRT: {},
			TRC: {},
			GFA: {},
			PJ: {},
		},
		selectedOffersIds: [],
	});

	const hasDerogations = derogationsForm.derogations.length > 0;

	const selectedPricingDetailIds = useMemo(() => {
		if (!hasDerogations || !constructionSiteOffersProducts) return [];
		return constructionSiteOffersProducts
			.filter((offer) => derogationsForm.selectedOffersIds.includes(offer.id))
			.map(
				(offer) =>
					offer.pricingDetails.find(
						(pricingDetail) => pricingDetail.type === "INITIAL",
					)?.id,
			)
			.filter((id): id is number => id != null);
	}, [
		hasDerogations,
		constructionSiteOffersProducts,
		derogationsForm.selectedOffersIds,
	]);

	const pricingQueries = usePricingDetailsQueries(selectedPricingDetailIds);
	const isPricingLoading = pricingQueries.some((query) => query.isLoading);

	const isPricingFormSynced = useMemo(() => {
		if (!constructionSiteOffersProducts) return true;
		return constructionSiteOffersProducts
			.filter((offer) =>
				derogationsForm.selectedOffersIds.includes(offer.id),
			)
			.every((offer) => {
				const warrantyType = offer.binderWarranty?.warrantyType;
				if (!warrantyType || !isOfferWarrantyType(warrantyType))
					return true;
				const hasPricingDetail = offer.pricingDetails.some(
					(pricingDetail) => pricingDetail.type === "INITIAL",
				);
				if (!hasPricingDetail) return true;
				const formValues =
					derogationsForm.newPricingDetails[warrantyType];
				return Object.keys(formValues).length > 0;
			});
	}, [
		constructionSiteOffersProducts,
		derogationsForm.selectedOffersIds,
		derogationsForm.newPricingDetails,
	]);

	const isActionDisabled =
		updateDerogationsMutation.isPending ||
		isPricingLoading ||
		!isPricingFormSynced;

	useEffect(() => {
		if (!Array.isArray(existingDerogations)) return;
		if (!constructionSiteOffersProducts) return;
		if (existingDerogations.length === 0) {
			setDerogationsForm({
				derogations: [],
				newPricingDetails: {
					DO: {},
					MRT: {},
					TRC: {},
					GFA: {},
					PJ: {},
				},
				selectedOffersIds: [],
			});
			return;
		}

		const derogationsFormFromApi: DerogationsForm = {
			newPricingDetails: {
				DO: {},
				MRT: {},
				TRC: {},
				GFA: {},
				PJ: {},
			},
			selectedOffersIds: Array.from(
				new Set(
					existingDerogations.flatMap(
						(derogation) => derogation.selectedOffersIds,
					),
				),
			),
			derogations: existingDerogations.map((derogation) => {
				return {
					enabled: Boolean(derogation.enabled),
					type: derogation.type,
					newConstructionCost:
						derogation.newConstructionCost != null
							? String(derogation.newConstructionCost)
							: "",
					newDeliveryDate:
						derogation.newDeliveryDate != null
							? format(derogation.newDeliveryDate, "yyyy-MM-dd")
							: "",
				};
			}),
		};

		setDerogationsForm(derogationsFormFromApi);
	}, [existingDerogations, constructionSiteOffersProducts]);

	useEffect(() => {
		const constructionSiteDetails = constructionSite?.constructionSiteDetails;
		if (!constructionSiteDetails) return;
		setDerogationsForm((previousForms) => ({
			newPricingDetails: previousForms.newPricingDetails,
			selectedOffersIds: previousForms.selectedOffersIds,
			derogations: previousForms.derogations.map((form) => {
				let newConstructionCostValue = form.newConstructionCost;
				let newDeliveryDateValue = form.newDeliveryDate;
				if (
					form.type === DerogationType.CONSTRUCTION_COST &&
					newConstructionCostValue === ""
				) {
					const fallbackCost =
						constructionSiteDetails.totalAmountTTC ??
						constructionSiteDetails.totalAmountHT;
					if (fallbackCost != null)
						newConstructionCostValue = String(fallbackCost);
				}
				if (
					form.type === DerogationType.DURATION &&
					newDeliveryDateValue === ""
				) {
					const fallbackDeliveryDate = constructionSiteDetails.deliveryDate
						?.toString()
						.split("T")[0];

					if (fallbackDeliveryDate) newDeliveryDateValue = fallbackDeliveryDate;
				}
				return {
					...form,
					newConstructionCost: newConstructionCostValue,
					newDeliveryDate: newDeliveryDateValue,
				};
			}),
		}));
	}, [constructionSite]);

	const buildDerogationPayloads = (): UpdateConstructionSiteDerogationsBody => {
		if (constructionSiteOffersProducts) {
			const hasUnsyncedPricing = constructionSiteOffersProducts
				.filter((offer) => derogationsForm.selectedOffersIds.includes(offer.id))
				.some((offer) => {
					const warrantyType = offer.binderWarranty?.warrantyType;
					if (!warrantyType || !isOfferWarrantyType(warrantyType)) return false;
					const hasPricingDetail = offer.pricingDetails.some(
						(pricingDetail) => pricingDetail.type === "INITIAL",
					);
					if (!hasPricingDetail) return false;
					return (
						Object.keys(derogationsForm.newPricingDetails[warrantyType])
							.length === 0
					);
				});
			if (hasUnsyncedPricing) {
				throw new Error(
					"Les données de tarification ne sont pas encore synchronisées",
				);
			}
		}
		const {
			totalClientPrimeHT: doTotalClientPrimeHT,
			totalClientPrimeTaxes: doTotalClientPrimeTaxes,
			totalClientPrimeTTC: doTotalClientPrimeTTC,
		} = calculateTotal(derogationsForm.newPricingDetails.DO, "DO");
		const {
			totalClientPrimeHT: mrtTotalClientPrimeHT,
			totalClientPrimeTaxes: mrtTotalClientPrimeTaxes,
			totalClientPrimeTTC: mrtTotalClientPrimeTTC,
		} = calculateTotal(derogationsForm.newPricingDetails.MRT, "MRT");
		const {
			totalClientPrimeHT: trcTotalClientPrimeHT,
			totalClientPrimeTaxes: trcTotalClientPrimeTaxes,
			totalClientPrimeTTC: trcTotalClientPrimeTTC,
		} = calculateTotal(derogationsForm.newPricingDetails.TRC, "TRC");
		const {
			totalClientPrimeHT: pjTotalClientPrimeHT,
			totalClientPrimeTaxes: pjTotalClientPrimeTaxes,
			totalClientPrimeTTC: pjTotalClientPrimeTTC,
		} = calculateTotal(derogationsForm.newPricingDetails.PJ, "PJ");

		return {
			derogations: derogationsForm.derogations
				.map((form) => {
					if (!form.type) return null;
					return {
						enabled: form.enabled,
						type: form.type,
						newConstructionCost:
							form.type === DerogationType.CONSTRUCTION_COST
								? form.newConstructionCost
									? Number(form.newConstructionCost)
									: null
								: undefined,
						newDeliveryDate:
							form.type === DerogationType.DURATION
								? form.newDeliveryDate
									? form.newDeliveryDate
									: null
								: undefined,
					};
				})
				.filter((payload) => payload !== null),
			newPricingDetails: {
				DO: {
					...pricingDetailsFormValuesToCreatePayload(
						derogationsForm.newPricingDetails.DO,
						"DO",
					),
					totalClientPrimeHT: doTotalClientPrimeHT,
					totalClientPrimeTaxes: doTotalClientPrimeTaxes,
					totalClientPrimeTTC: doTotalClientPrimeTTC,
				},
				MRT: {
					...pricingDetailsFormValuesToCreatePayload(
						derogationsForm.newPricingDetails.MRT,
						"MRT",
					),
					totalClientPrimeHT: mrtTotalClientPrimeHT,
					totalClientPrimeTaxes: mrtTotalClientPrimeTaxes,
					totalClientPrimeTTC: mrtTotalClientPrimeTTC,
				},
				TRC: {
					...pricingDetailsFormValuesToCreatePayload(
						derogationsForm.newPricingDetails.TRC,
						"TRC",
					),
					totalClientPrimeHT: trcTotalClientPrimeHT,
					totalClientPrimeTaxes: trcTotalClientPrimeTaxes,
					totalClientPrimeTTC: trcTotalClientPrimeTTC,
				},
				GFA: {},
				PJ: {
					...pricingDetailsFormValuesToCreatePayload(
						derogationsForm.newPricingDetails.PJ,
						"PJ",
					),
					totalClientPrimeHT: pjTotalClientPrimeHT,
					totalClientPrimeTaxes: pjTotalClientPrimeTaxes,
					totalClientPrimeTTC: pjTotalClientPrimeTTC,
				},
			},
			selectedOffersIds: derogationsForm.selectedOffersIds,
		};
	};

	const saveDerogations = async (onSaveSuccess: () => void) => {
		try {
			const derogationPayloads = buildDerogationPayloads();
			await updateDerogationsMutation.mutateAsync(derogationPayloads);

			onSaveSuccess();
		} finally {
			setCurrentAction(null);
		}
	};

	const hasMissingDerogationType = () =>
		derogationsForm.derogations.some((form) => form.type == null);

	const handleSaveAndNext = async () => {
		setCurrentAction("saveAndNext");
		if (hasMissingDerogationType()) {
			setShowValidationErrors(true);
			setCurrentAction(null);
			return;
		}
		setShowValidationErrors(false);
		await saveDerogations(handleNextStep);
	};

	const handleSaveAndClose = async () => {
		setCurrentAction("saveAndClose");
		if (hasMissingDerogationType()) {
			setShowValidationErrors(true);
			setCurrentAction(null);
			return;
		}
		setShowValidationErrors(false);
		await saveDerogations(handleClose);
	};

	const getDerogationFormCompletedWithInitialConstructionSiteValues = (
		newDerogation: DerogationForm,
	) => {
		if (newDerogation.type === DerogationType.CONSTRUCTION_COST) {
			return {
				...newDerogation,
				newConstructionCost: String(
					constructionSite?.constructionSiteDetails?.totalAmountTTC ??
						constructionSite?.constructionSiteDetails?.totalAmountHT ??
						"",
				),
			};
		}
		if (newDerogation.type === DerogationType.DURATION) {
			return {
				...newDerogation,
				newDeliveryDate: constructionSite?.constructionSiteDetails?.deliveryDate
					? format(
							constructionSite?.constructionSiteDetails?.deliveryDate,
							"yyyy-MM-dd",
						)
					: "",
			};
		}
		return newDerogation;
	};

	const handleFormChange = (newForm: DerogationsForm) => {
		setDerogationsForm((previousForm) => {
			const { derogations: newDerogations, ...rest } = newForm;
			const finalNewForm = {
				...rest,
				derogations: newDerogations.map((newDerogation, newIndex) => {
					const previousDerogation = previousForm.derogations[newIndex];
					if (previousDerogation.type !== newDerogation.type) {
						return getDerogationFormCompletedWithInitialConstructionSiteValues(
							newDerogation,
						);
					}
					return newDerogation;
				}),
			};
			return finalNewForm;
		});
	};

	const createEmptyDerogationForm = useCallback(
		(): DerogationForm => ({
			enabled: true,
			type: null,
			newConstructionCost: "",
			newDeliveryDate: "",
		}),
		[],
	);

	const handleAddDerogation = () => {
		setDerogationsForm((previousForm) => ({
			...previousForm,
			derogations: [...previousForm.derogations, createEmptyDerogationForm()],
		}));
	};

	const handleRemoveForm = (index: number) => {
		setDerogationsForm((previousForm) => ({
			...previousForm,
			derogations: previousForm.derogations.filter(
				(_, currentIndex) => currentIndex !== index,
			),
		}));
	};

	return (
		<div className="flex flex-col gap-8">
			<h2 className="text-2xl font-medium text-text-secondary-blue">
				Dérogation
			</h2>

			<div className="flex flex-col gap-6">
				<DerogationsCard
					offers={constructionSiteOffersProducts}
					onFormChange={handleFormChange}
					onRemoveDerogation={handleRemoveForm}
					onAddDerogation={handleAddDerogation}
					derogationsForm={derogationsForm}
					showErrors={showValidationErrors}
					constructionSiteStartDate={
						constructionSite?.constructionSiteDetails?.startDate
							? format(
									constructionSite?.constructionSiteDetails?.startDate,
									"yyyy-MM-dd",
								)
							: null
					}
					constructionSiteId={Number(constructionSiteId)}
				/>
			</div>

			<div className="flex flex-row h-11 items-center justify-between w-full">
				<Button
					variant="outline"
					onClick={handlePreviousStep}
					disabled={updateDerogationsMutation.isPending}
				>
					Précédent
				</Button>
				<div className="flex flex-row gap-4 items-center">
					<Button
						variant="outline"
						onClick={handleSaveAndClose}
						disabled={isActionDisabled}
						isLoading={currentAction === "saveAndClose"}
					>
						Enregistrer et fermer
					</Button>
					<Button
						onClick={handleSaveAndNext}
						disabled={isActionDisabled}
						isLoading={currentAction === "saveAndNext"}
					>
						Suivant
					</Button>
				</div>
			</div>
		</div>
	);
};
